import React, { createContext, useContext, useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { auth, db } from '../firebase/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { normalizeCompanyIdForStorage } from '../utils/companyBrandingUpload';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  /** companies/{id} snapshot loaded in the same bootstrap as user doc (before loading false). */
  const [companyBootstrap, setCompanyBootstrap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    console.log('🔐 AuthProvider: Setting up auth state listener...');

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔐 Auth state changed:', firebaseUser ? `User: ${firebaseUser.uid}` : 'No user');

      if (firebaseUser) {
        // After a prior `null` auth event, `loading` can already be false — turn it back on until bootstrap finishes.
        flushSync(() => {
          setLoading(true);
          setUser(firebaseUser);
        });

        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const firestoreUserData = userDocSnap.data();
            const companyIdStr = normalizeCompanyIdForStorage(firestoreUserData.companyId);

            let bootstrap = null;
            if (companyIdStr) {
              try {
                const companySnap = await getDoc(doc(db, 'companies', companyIdStr));
                bootstrap = companySnap.exists() ? companySnap.data() : null;
              } catch (e) {
                console.error('❌ Error loading company for session bootstrap', e);
                bootstrap = null;
              }
            }

            flushSync(() => {
              setCompanyBootstrap(bootstrap);
              setUserData({
                ...firestoreUserData,
                companyId: companyIdStr || null,
              });
            });
            console.log('✅ User + company bootstrap loaded:', {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              role: firestoreUserData.role,
              companyId: companyIdStr || null,
              hasCompanyDoc: bootstrap != null,
            });
          } else {
            console.warn('⚠️ User document not found in Firestore');
            flushSync(() => {
              setUserData(null);
              setCompanyBootstrap(null);
            });
          }
        } catch (error) {
          console.error('❌ Error fetching user data:', error);
          flushSync(() => {
            setUserData(null);
            setCompanyBootstrap(null);
          });
        }
      } else {
        flushSync(() => {
          setUser(null);
          setUserData(null);
          setCompanyBootstrap(null);
        });
        console.log('🔓 User signed out');
      }

      // Paint with committed userData + companyBootstrap before hiding the splash (BrandingContext).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setLoading(false);
          setInitialized(true);
        });
      });
    });

    return () => {
      console.log('🔐 AuthProvider: Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  const value = {
    user,
    userData,
    companyBootstrap,
    loading,
    initialized,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
